import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../components/Card";
import {
  createProgramDraft,
  getProgramDiff,
  getReferralSettings,
  listProgramVersions,
  listPrograms,
  publishProgram,
  updateProgramDraft,
  updateReferralSettings,
} from "../../api/referrals.admin.service";
import { askReason, errorCodeOf, errorDetailsOf, isOk, StatusBadge, useErrorText, useFormat } from "./shared";
import styles from "./Referrals.module.sass";

const pretty = (v) => JSON.stringify(v, null, 2);

/**
 * Programas y ajustes (06 §2.6). Flujo: borrador → guardar (la API valida rangos
 * y coherencia) → DIFF contra la versión activa → publicar. El botón de publicar
 * solo se habilita tras ver el diff del borrador guardado.
 */
const ProgramsTab = () => {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [programs, setPrograms] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    const resp = await listPrograms();
    if (isOk(resp)) setPrograms(resp.data);
    else setError(errorCodeOf(resp));
  };
  useEffect(() => {
    load();
  }, []);

  if (error) return <div className={styles.empty}>{errorText(error)}</div>;
  if (!programs) return <div className={styles.empty}>{t("referrals.common.loading")}</div>;

  return (
    <>
      {programs.map((p) => (
        <ProgramCard key={p.key} program={p} onChanged={load} />
      ))}
      <SettingsCard />
    </>
  );
};

const ProgramCard = ({ program, onChanged }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const { key, active, draft } = program;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState(null);

  const fail = (resp) => {
    setError(errorCodeOf(resp));
    setDetails(errorDetailsOf(resp));
  };
  const clearErrors = () => {
    setError(null);
    setDetails([]);
  };

  const startEditing = async () => {
    clearErrors();
    let d = draft;
    if (!d) {
      setBusy(true);
      const resp = await createProgramDraft(key);
      setBusy(false);
      if (!isOk(resp)) return fail(resp);
      d = resp.data;
      onChanged();
    }
    setText(pretty(d.params));
    setNotes(d.notes || "");
    setDirty(false);
    setDiff(null);
    setEditing(d.version);
  };

  const save = async () => {
    clearErrors();
    let params;
    try {
      params = JSON.parse(text);
    } catch (e) {
      setError("invalid_json");
      return;
    }
    setBusy(true);
    const resp = await updateProgramDraft(key, editing, params, notes);
    setBusy(false);
    if (!isOk(resp)) return fail(resp);
    setDirty(false);
    setDiff(null);
    onChanged();
  };

  const loadDiff = async () => {
    clearErrors();
    const resp = await getProgramDiff(key, editing);
    if (!isOk(resp)) return fail(resp);
    setDiff(resp.data);
  };

  const publish = async () => {
    const reason = askReason(t);
    if (!reason) return;
    clearErrors();
    setBusy(true);
    const resp = await publishProgram(key, editing, reason);
    setBusy(false);
    if (!isOk(resp)) return fail(resp);
    setEditing(false);
    setDiff(null);
    onChanged();
  };

  const toggleVersions = async () => {
    if (versions) return setVersions(null);
    const resp = await listProgramVersions(key);
    if (isOk(resp)) setVersions(resp.data);
    else fail(resp);
  };

  const p = active?.params;
  return (
    <Card
      className={styles.card}
      title={t(`referrals.program.${key}`)}
      classTitle={key === "PARTNER" ? "title-purple" : key === "FRIEND" ? "title-green" : "title-yellow"}
      head={
        <div className={styles.rowActions}>
          <button type="button" className={styles.linkBtn} onClick={toggleVersions}>
            {t("referrals.programs.history")}
          </button>
          {!editing && (
            <button type="button" className="button-stroke button-small" disabled={busy} onClick={startEditing}>
              {draft ? t("referrals.programs.editDraft", { version: draft.version }) : t("referrals.programs.newDraft")}
            </button>
          )}
        </div>
      }
    >
      {active ? (
        <div className={styles.facts}>
          <div>
            <span className={styles.muted}>{t("referrals.programs.activeVersion")}</span>
            <strong>
              v{active.version} · {p.enabled ? t("referrals.programs.on") : t("referrals.programs.off")}
            </strong>
          </div>
          <div>
            <span className={styles.muted}>{t("referrals.programs.beta")}</span>
            <strong>{p.betaUserIds?.length ? t("referrals.programs.betaUsers", { count: p.betaUserIds.length }) : t("referrals.programs.open")}</strong>
          </div>
          <div>
            <span className={styles.muted}>{t("referrals.programs.welcome")}</span>
            <strong>{p.welcomeReward ? `${p.welcomeReward.days} d` : "—"}</strong>
          </div>
          <div>
            <span className={styles.muted}>{t("referrals.programs.discount")}</span>
            <strong>
              {p.refereeDiscount?.enabled
                ? `${p.refereeDiscount.percent} % × ${p.refereeDiscount.periods}`
                : t("referrals.programs.off")}
            </strong>
          </div>
          {p.inviterReward && (
            <div>
              <span className={styles.muted}>{t("referrals.programs.inviterReward")}</span>
              <strong>
                {p.inviterReward.days} d · {p.inviterReward.trigger}
              </strong>
            </div>
          )}
          {p.commission && (
            <div>
              <span className={styles.muted}>{t("referrals.programs.commission")}</span>
              <strong>
                {p.commission.ratePctByPlan.MONTHLY}/{p.commission.ratePctByPlan.YEARLY}/{p.commission.ratePctByPlan.LIFETIME} % ·{" "}
                {p.commission.windowMonths} m
              </strong>
            </div>
          )}
          <div>
            <span className={styles.muted}>{t("referrals.programs.publishedAt")}</span>
            <strong>{fmt.date(active.publishedAt)}</strong>
          </div>
        </div>
      ) : (
        <div className={styles.empty}>{t("referrals.programs.noActive")}</div>
      )}

      {versions && (
        <div className={styles.table}>
          {versions.map((v) => (
            <div key={v.version} className={styles.row}>
              <code className={styles.codeCell}>v{v.version}</code>
              <div className={styles.rowMain}>
                <div className={styles.muted}>
                  {v.publishedAt ? t("referrals.programs.publishedOn", { date: fmt.dateTime(v.publishedAt) }) : fmt.dateTime(v.createdAt)}
                  {v.notes ? ` · ${v.notes}` : ""}
                </div>
              </div>
              <StatusBadge status={v.status} />
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className={styles.editor}>
          <div className={styles.muted}>{t("referrals.programs.editorHint", { version: editing })}</div>
          <textarea
            className={styles.jsonEditor}
            value={text}
            spellCheck={false}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(true);
              setDiff(null);
            }}
          />
          <input
            className={styles.search}
            placeholder={t("referrals.programs.notes")}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setDirty(true);
            }}
          />
          {error && (
            <div className={styles.errorBox}>
              {error === "invalid_json" ? t("referrals.programs.invalidJson") : errorText(error)}
              {details.map((d) => (
                <div key={d.path + d.message}>
                  <code>{d.path}</code>: {d.message}
                </div>
              ))}
            </div>
          )}
          {diff && (
            <div className={styles.diff}>
              <div className={styles.sectionTitle}>
                {t("referrals.programs.diffTitle", { base: diff.base ?? "—", version: editing })}
              </div>
              {diff.changes.length === 0 && <div className={styles.muted}>{t("referrals.programs.noChanges")}</div>}
              {diff.changes.map((c) => (
                <div key={c.path} className={styles.diffRow}>
                  <code>{c.path}</code>
                  <span className={styles.diffBefore}>{JSON.stringify(c.before)}</span>
                  <span>→</span>
                  <span className={styles.diffAfter}>{JSON.stringify(c.after)}</span>
                </div>
              ))}
            </div>
          )}
          <div className={styles.actions}>
            <button type="button" className="button-stroke button-small" disabled={busy || !dirty} onClick={save}>
              {t("referrals.programs.save")}
            </button>
            <button type="button" className="button-stroke button-small" disabled={busy || dirty} onClick={loadDiff}>
              {t("referrals.programs.viewDiff")}
            </button>
            <button type="button" className="button button-small" disabled={busy || dirty || !diff} onClick={publish}>
              {t("referrals.programs.publish")}
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => setEditing(false)}>
              {t("referrals.common.close")}
            </button>
          </div>
          {!diff && !dirty && <div className={styles.muted}>{t("referrals.programs.diffRequired")}</div>}
        </div>
      )}
    </Card>
  );
};

const SettingsCard = () => {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [settings, setSettings] = useState(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    const resp = await getReferralSettings();
    if (!isOk(resp)) return setError(errorCodeOf(resp));
    const { version, updatedAt, updatedBy, ...body } = resp.data;
    setSettings(resp.data);
    setText(pretty(body));
  };
  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async () => {
    setError(null);
    setDetails([]);
    setSaved(false);
    let body;
    try {
      body = JSON.parse(text);
    } catch (e) {
      return setError("invalid_json");
    }
    const reason = askReason(t);
    if (!reason) return;
    const resp = await updateReferralSettings(body, settings.version, reason);
    if (!isOk(resp)) {
      setError(errorCodeOf(resp));
      setDetails(errorDetailsOf(resp));
      return;
    }
    setSaved(true);
    load();
  };

  return (
    <Card
      className={styles.card}
      title={t("referrals.programs.settings")}
      classTitle="title-blue"
      head={
        <button type="button" className={styles.linkBtn} onClick={() => setOpen(!open)}>
          {open ? t("referrals.common.close") : t("referrals.programs.editSettings")}
        </button>
      }
    >
      <div className={styles.muted}>{t("referrals.programs.settingsHint")}</div>
      {open && settings && (
        <div className={styles.editor}>
          <div className={styles.muted}>v{settings.version}</div>
          <textarea className={styles.jsonEditor} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} />
          {error && (
            <div className={styles.errorBox}>
              {error === "invalid_json" ? t("referrals.programs.invalidJson") : errorText(error)}
              {details.map((d) => (
                <div key={d.path + d.message}>
                  <code>{d.path}</code>: {d.message}
                </div>
              ))}
            </div>
          )}
          {saved && <div className={styles.okBox}>{t("referrals.programs.saved")}</div>}
          <div className={styles.actions}>
            <button type="button" className="button button-small" onClick={save}>
              {t("referrals.programs.save")}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ProgramsTab;
